import { ItemDetailsData } from "./DefinitionsInterfaces";

// Coupon data interface
export interface CouponData {
  isApplied: boolean;
  code: string;
  discountAmount: number;
}

// Bundle info interface
export interface BundleInfo {
  isBundle: boolean;
  primaryProductId: number | null;
}

// Pricing calculation result interface
export interface PricingResult {
  basePrice: number;
  sellingPrice: number;
  discountedPrice: number;
  bundleDiscount: number;
  prepaidDiscount: number;
  couponDiscount: number;
  finalPrice: number;
  totalDiscount: number;
  discountPercentage: number;
}

// Individual item pricing result
export interface ItemPricingResult {
  itemId: number;
  basePrice: number;
  displayPrice: number;
  isBundlePriceApplied: boolean;
  discountAmount: number;
  discountPercentage: number;
}

// Cart pricing summary
export interface CartPricingSummary {
  items: ItemPricingResult[];
  subtotal: PricingResult;
  couponData: CouponData;
  bundleInfo: BundleInfo;
}

class PricingService {
  /**
   * Get coupon data from session storage
   */
  static getCouponData(): CouponData {
    const appliedCouponCode = sessionStorage.getItem("appliedCouponCode");
    const couponDiscountAmount = sessionStorage.getItem("couponDiscountAmount");

    if (appliedCouponCode && couponDiscountAmount) {
      return {
        isApplied: true,
        code: appliedCouponCode,
        discountAmount: parseFloat(couponDiscountAmount),
      };
    }

    return {
      isApplied: false,
      code: "",
      discountAmount: 0,
    };
  }

  /**
   * Check if bundle pricing should apply to a specific item
   */
  static shouldShowBundlePrice(
    itemId: number,
    bundleInfo: BundleInfo,
    checkOutItems: ItemDetailsData[]
  ): boolean {
    if (!bundleInfo.isBundle || !bundleInfo.primaryProductId) return false;

    // Don't apply bundle pricing to the primary product itself
    if (itemId === bundleInfo.primaryProductId) return false;

    // Check if primary product is in the cart
    const primaryProductInCart = checkOutItems.some(
      (item) => item.id === bundleInfo.primaryProductId
    );

    return primaryProductInCart;
  }

  /**
   * Get display price for an item (considering bundle pricing)
   */
  static getDisplayPrice(
    item: ItemDetailsData,
    bundleInfo: BundleInfo,
    checkOutItems: ItemDetailsData[]
  ): number {
    console.log(`getDisplayPrice for item ${item.id}:`, {
      id: item.id,
      name: item.name,
      selling_price: item.selling_price,
      discountedBundlePrice: item.discountedBundlePrice,
      bundleInfo: bundleInfo,
    });

    // For items with id === 3, always use selling_price regardless of bundle pricing
    if (item.id === 3) {
      console.log(`Item ${item.id} using selling_price:`, item.selling_price);
      return item.selling_price;
    }

    const shouldUseBundlePrice = this.shouldShowBundlePrice(
      item.id,
      bundleInfo,
      checkOutItems
    );
    const finalPrice = shouldUseBundlePrice
      ? item.discountedBundlePrice
      : item.selling_price;

    console.log(
      `Item ${item.id} shouldUseBundlePrice: ${shouldUseBundlePrice}, finalPrice: ${finalPrice}`
    );
    return finalPrice;
  }

  /**
   * Calculate pricing for a single item
   */
  static calculateItemPricing(
    item: ItemDetailsData,
    bundleInfo: BundleInfo,
    checkOutItems: ItemDetailsData[]
  ): ItemPricingResult {
    const displayPrice = this.getDisplayPrice(item, bundleInfo, checkOutItems);
    const isBundlePriceApplied = this.shouldShowBundlePrice(
      item.id,
      bundleInfo,
      checkOutItems
    );
    const discountAmount = item.base_price - displayPrice;
    const discountPercentage = Math.round(
      (discountAmount / item.base_price) * 100
    );

    return {
      itemId: item.id,
      basePrice: item.base_price,
      displayPrice: displayPrice,
      isBundlePriceApplied: isBundlePriceApplied,
      discountAmount: discountAmount,
      discountPercentage: discountPercentage,
    };
  }

  /**
   * Calculate prepaid discount amount
   */
  // static calculatePrepaidDiscount(price: number): number {
  //   return Math.round(price * 0.05); // 5% discount
  // }

  /**
   * Calculate complete cart pricing
   */
  static calculateCartPricing(
    checkOutItems: ItemDetailsData[],
    bundleInfo: BundleInfo,
    couponData?: CouponData,
    paymentType: "prepaid" | "cod" = "prepaid"
  ): CartPricingSummary {
    console.log("=== PRICING DEBUG ===");
    console.log("checkOutItems:", checkOutItems);
    console.log("bundleInfo:", bundleInfo);

    // Calculate individual item pricing
    const items = checkOutItems.map((item) => {
      const itemPricing = this.calculateItemPricing(
        item,
        bundleInfo,
        checkOutItems
      );
      console.log(`Item ${item.id} (${item.name}):`, {
        basePrice: itemPricing.basePrice,
        displayPrice: itemPricing.displayPrice,
        discountAmount: itemPricing.discountAmount,
        isBundlePriceApplied: itemPricing.isBundlePriceApplied,
        selling_price: item.selling_price,
        discountedBundlePrice: item.discountedBundlePrice,
      });
      return itemPricing;
    });

    // Calculate subtotals
    const basePrice = items.reduce((sum, item) => sum + item.basePrice, 0);
    const sellingPrice = items.reduce(
      (sum, item) => sum + item.displayPrice,
      0
    );

    console.log("Total basePrice:", basePrice);
    console.log("Total sellingPrice:", sellingPrice);
    const bundleDiscount = items.reduce(
      (sum, item) =>
        sum + (item.isBundlePriceApplied ? item.discountAmount : 0),
      0
    );

    // Calculate prepaid discount (5% of selling price after bundle discounts)
    // const prepaidDiscount =
    //   paymentType === "prepaid"
    //     ? this.calculatePrepaidDiscount(sellingPrice)
    //     : 0;
    const prepaidDiscount = 0; // Prepaid discount temporarily disabled
    const priceAfterPrepaidDiscount = sellingPrice - prepaidDiscount;

    console.log("prepaidDiscount:", prepaidDiscount);
    console.log("priceAfterPrepaidDiscount:", priceAfterPrepaidDiscount);

    // Get coupon data if not provided
    const coupon = couponData || this.getCouponData();
    const couponDiscount = coupon.isApplied ? coupon.discountAmount : 0;

    console.log("coupon:", coupon);
    console.log("couponDiscount:", couponDiscount);

    // Calculate final price
    const finalPrice = priceAfterPrepaidDiscount - couponDiscount;

    console.log("FINAL PRICE:", finalPrice);
    console.log("=== END PRICING DEBUG ===");
    const totalDiscount = basePrice - finalPrice;
    const discountPercentage = Math.round((totalDiscount / basePrice) * 100);

    const subtotal: PricingResult = {
      basePrice,
      sellingPrice,
      discountedPrice: priceAfterPrepaidDiscount,
      bundleDiscount,
      prepaidDiscount,
      couponDiscount,
      finalPrice,
      totalDiscount,
      discountPercentage,
    };

    return {
      items,
      subtotal,
      couponData: coupon,
      bundleInfo,
    };
  }

  /**
   * Check if bundle pricing is active
   */
  static isBundlePricingActive(
    bundleInfo: BundleInfo,
    checkOutItems: ItemDetailsData[]
  ): boolean {
    if (!bundleInfo.isBundle || !bundleInfo.primaryProductId) return false;

    return checkOutItems.some(
      (item) => item.id === bundleInfo.primaryProductId
    );
  }

  /**
   * Get bundle pricing parameters for API calls
   */
  static getBundleParams(
    bundleInfo: BundleInfo,
    checkOutItems: ItemDetailsData[]
  ): { giveBundleDiscount: boolean; primaryProductID?: number } {
    if (!bundleInfo.isBundle || !bundleInfo.primaryProductId) {
      return { giveBundleDiscount: false };
    }

    // Check if primary product is in the cart
    const primaryProductInCart = checkOutItems.some(
      (item) => item.id === bundleInfo.primaryProductId
    );

    if (primaryProductInCart) {
      return {
        giveBundleDiscount: true,
        primaryProductID: bundleInfo.primaryProductId,
      };
    }

    return { giveBundleDiscount: false };
  }

  /**
   * Calculate current amount for coupon validation (after bundle discounts and prepaid discount but before coupon)
   */
  static calculateCurrentAmount(
    checkOutItems: ItemDetailsData[],
    bundleInfo: BundleInfo,
    paymentType: "prepaid" | "cod" = "prepaid"
  ): number {
    const baseAmount = checkOutItems.reduce((total, item) => {
      const effectivePrice = this.getDisplayPrice(
        item,
        bundleInfo,
        checkOutItems
      );
      return total + effectivePrice;
    }, 0);

    // Apply prepaid discount if applicable
    // const prepaidDiscount =
    //   paymentType === "prepaid" ? this.calculatePrepaidDiscount(baseAmount) : 0;
    // return baseAmount - prepaidDiscount;
    return baseAmount; // Prepaid discount temporarily disabled
  }

  /**
   * Get applied coupon code from session storage
   */
  static getAppliedCouponCode(): string | undefined {
    const appliedCouponCode = sessionStorage.getItem("appliedCouponCode");
    return appliedCouponCode || undefined;
  }

  /**
   * Format price for display
   */
  static formatPrice(price: number): string {
    return `₹${price}`;
  }

  /**
   * Calculate COD charges
   */
  static calculateCODCharges(itemCount: number): number {
    return itemCount > 1 ? 150 : 100;
  }

  /**
   * Calculate final order total including COD charges
   */
  static calculateOrderTotal(
    finalPrice: number,
    paymentType: "prepaid" | "cod",
    itemCount: number
  ): number {
    console.log("🧮 calculateOrderTotal called:", {
      finalPrice,
      paymentType,
      itemCount,
    });
    const codCharges =
      paymentType === "cod" ? this.calculateCODCharges(itemCount) : 0;
    const total = finalPrice + codCharges;
    console.log("🧮 calculateOrderTotal returning:", total);
    return total;
  }
}

export default PricingService;
